// src/components/games/crossroads/CombatPreview.tsx
// Combat damage preview showing predicted outcomes

import React from 'react';
import { IonCard, IonCardContent, IonText, IonBadge, IonIcon, IonButton } from '@ionic/react';
import {
  skullOutline,
  heartOutline,
  shieldOutline,
  alertCircleOutline,
  checkmarkCircleOutline,
  arrowForward,
} from 'ionicons/icons';
import { BattlefieldCreature, CombatDeclaration, BlockDeclaration, AbilityKeyword, GameState } from './types';
import './CombatPreview.css';

interface CombatOutcome {
  attackerId: string;
  attackerName: string;
  attackerDamage: number;
  attackerWillDie: boolean;
  targetId: string;
  targetName: string;
  targetDamage: number;
  targetWillDie: boolean;
  damageToCommander: number;
  hasFirstStrike: boolean;
  hasDeathtouch: boolean;
  hasTrample: boolean;
}

interface CombatPreviewProps {
  attackers: CombatDeclaration[];
  blockers: BlockDeclaration[];
  playerCreatures: BattlefieldCreature[];
  opponentCreatures: BattlefieldCreature[];
  playerCommanderHealth: number;
  opponentCommanderHealth: number;
  onConfirm: () => void;
  onCancel: () => void;
  isPlayerAttacking: boolean;
  gameState?: GameState;
}

export const CombatPreview: React.FC<CombatPreviewProps> = ({
  attackers,
  blockers,
  playerCreatures,
  opponentCreatures,
  playerCommanderHealth,
  opponentCommanderHealth,
  onConfirm,
  onCancel,
  isPlayerAttacking,
  gameState,
}) => {
  const allCreatures = [...playerCreatures, ...opponentCreatures];

  // Helper to get creature name from cardId
  const getCreatureName = (creature: BattlefieldCreature): string => {
    if (gameState?.cardInstances[creature.cardId]) {
      return gameState.cardInstances[creature.cardId].name;
    }
    return creature.cardId; // Fallback to cardId if name not found
  };

  const calculateCombatOutcomes = (): CombatOutcome[] => {
    const outcomes: CombatOutcome[] = [];

    attackers.forEach((attack) => {
      const attacker = allCreatures.find(c => c.instanceId === attack.attackerId);
      if (!attacker) return;

      const attackerCard = attacker;
      const hasFirstStrike = attackerCard.keywords.includes(AbilityKeyword.FIRST_STRIKE) ||
                            attackerCard.keywords.includes(AbilityKeyword.DOUBLE_STRIKE);
      const hasDeathtouch = attackerCard.keywords.includes(AbilityKeyword.DEATHTOUCH);
      const hasTrample = attackerCard.keywords.includes(AbilityKeyword.TRAMPLE);

      // Find blockers for this attacker
      const attackBlockers = blockers.filter(b => b.attackerId === attack.attackerId);

      if (attackBlockers.length > 0) {
        // Blocked - combat with creatures
        attackBlockers.forEach((block) => {
          const blocker = allCreatures.find(c => c.instanceId === block.blockerId);
          if (!blocker) return;

          const blockerHasFirstStrike = blocker.keywords.includes(AbilityKeyword.FIRST_STRIKE) ||
                                       blocker.keywords.includes(AbilityKeyword.DOUBLE_STRIKE);
          const blockerHasDeathtouch = blocker.keywords.includes(AbilityKeyword.DEATHTOUCH);

          let attackerDamage = blocker.power;
          let targetDamage = attacker.power;
          let attackerWillDie = false;
          let targetWillDie = false;

          // First strike handling
          if (hasFirstStrike && !blockerHasFirstStrike) {
            // Attacker deals damage first
            targetWillDie = hasDeathtouch || targetDamage >= blocker.currentHealth;
            if (!targetWillDie) {
              attackerWillDie = blockerHasDeathtouch || attackerDamage >= attacker.currentHealth;
            } else {
              attackerDamage = 0; // Blocker dies before dealing damage
            }
          } else if (blockerHasFirstStrike && !hasFirstStrike) {
            // Blocker deals damage first
            attackerWillDie = blockerHasDeathtouch || attackerDamage >= attacker.currentHealth;
            if (!attackerWillDie) {
              targetWillDie = hasDeathtouch || targetDamage >= blocker.currentHealth;
            } else {
              targetDamage = 0; // Attacker dies before dealing damage
            }
          } else {
            // Simultaneous damage
            attackerWillDie = blockerHasDeathtouch || attackerDamage >= attacker.currentHealth;
            targetWillDie = hasDeathtouch || targetDamage >= blocker.currentHealth;
          }

          outcomes.push({
            attackerId: attacker.instanceId,
            attackerName: getCreatureName(attacker),
            attackerDamage,
            attackerWillDie,
            targetId: blocker.instanceId,
            targetName: getCreatureName(blocker),
            targetDamage,
            targetWillDie,
            damageToCommander: 0,
            hasFirstStrike,
            hasDeathtouch,
            hasTrample,
          });
        });
      } else if (attack.targetType === 'player') {
        // Unblocked - damage to commander
        outcomes.push({
          attackerId: attacker.instanceId,
          attackerName: getCreatureName(attacker),
          attackerDamage: 0,
          attackerWillDie: false,
          targetId: 'commander',
          targetName: isPlayerAttacking ? 'Opponent Commander' : 'Your Commander',
          targetDamage: 0,
          targetWillDie: false,
          damageToCommander: attacker.power,
          hasFirstStrike,
          hasDeathtouch,
          hasTrample,
        });
      }
    });

    return outcomes;
  };

  const outcomes = calculateCombatOutcomes();
  const totalCommanderDamage = outcomes.reduce((sum, o) => sum + o.damageToCommander, 0);
  const yourCasualties = outcomes.filter(o => o.attackerWillDie && isPlayerAttacking).length +
                         outcomes.filter(o => o.targetWillDie && !isPlayerAttacking).length;
  const opponentCasualties = outcomes.filter(o => o.targetWillDie && isPlayerAttacking).length +
                            outcomes.filter(o => o.attackerWillDie && !isPlayerAttacking).length;

  const finalCommanderHealth = isPlayerAttacking
    ? opponentCommanderHealth - totalCommanderDamage
    : playerCommanderHealth - totalCommanderDamage;

  const isLethal = finalCommanderHealth <= 0;

  return (
    <div className="combat-preview-overlay">
      <IonCard className="combat-preview-card">
        <IonCardContent>
          <div className="combat-preview-header">
            <IonText>
              <h2 style={{ margin: 0, fontSize: '1.2em' }}>
                <IonIcon icon={alertCircleOutline} style={{ marginRight: '8px' }} />
                Combat Preview
              </h2>
            </IonText>
          </div>

          {/* Summary */}
          <div className="combat-summary">
            <div className="combat-summary-item">
              <IonIcon icon={heartOutline} color={isLethal ? 'danger' : 'medium'} />
              <IonText>
                <strong>Commander Damage:</strong> {totalCommanderDamage}
              </IonText>
              {isLethal && (
                <IonBadge color="danger" style={{ marginLeft: '8px' }}>
                  LETHAL!
                </IonBadge>
              )}
            </div>
            <div className="combat-summary-item">
              <IonIcon icon={skullOutline} color="danger" />
              <IonText>
                <strong>Your Casualties:</strong> {yourCasualties}
              </IonText>
            </div>
            <div className="combat-summary-item">
              <IonIcon icon={skullOutline} color="success" />
              <IonText>
                <strong>Opponent Casualties:</strong> {opponentCasualties}
              </IonText>
            </div>
          </div>

          {/* Detailed outcomes */}
          <div className="combat-outcomes">
            {outcomes.map((outcome, idx) => (
              <div key={idx} className="combat-outcome-item">
                <div className="combatant attacker">
                  <IonText>
                    <strong>{outcome.attackerName}</strong>
                  </IonText>
                  {outcome.attackerDamage > 0 && (
                    <IonBadge color="danger">
                      -{outcome.attackerDamage} HP
                    </IonBadge>
                  )}
                  {outcome.attackerWillDie && (
                    <IonBadge color="dark">
                      <IonIcon icon={skullOutline} /> Dies
                    </IonBadge>
                  )}
                </div>

                <IonIcon icon={arrowForward} className="combat-arrow" />

                <div className="combatant target">
                  <IonText>
                    <strong>{outcome.targetName}</strong>
                  </IonText>
                  {outcome.targetDamage > 0 && (
                    <IonBadge color="danger">
                      -{outcome.targetDamage} HP
                    </IonBadge>
                  )}
                  {outcome.damageToCommander > 0 && (
                    <IonBadge color="danger">
                      -{outcome.damageToCommander} HP
                    </IonBadge>
                  )}
                  {outcome.targetWillDie && (
                    <IonBadge color="dark">
                      <IonIcon icon={skullOutline} /> Dies
                    </IonBadge>
                  )}
                </div>
              </div>
            ))}
          </div>

          {outcomes.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <IonText color="medium">
                <p>No combat to resolve</p>
              </IonText>
            </div>
          )}

          {/* Actions */}
          <div className="combat-preview-actions">
            <IonButton expand="block" color="success" onClick={onConfirm}>
              <IonIcon icon={checkmarkCircleOutline} slot="start" />
              Confirm Attack{isLethal && ' - WIN GAME!'}
            </IonButton>
            <IonButton expand="block" color="medium" fill="outline" onClick={onCancel}>
              Cancel
            </IonButton>
          </div>
        </IonCardContent>
      </IonCard>
    </div>
  );
};

export default CombatPreview;
